import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';

import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import {
    CustomFieldTypeView,
    EntityCustomFieldValueView,
    EntityTypeCustomFieldSectionView,
    EntityTypeCustomFieldView,
    SearchEntityTypeCustomFieldParameters
} from '@api-proxies';
import { EntityTypeCustomFieldService } from '@http/clients/entity-type-custom-field.service';
import { CustomFieldFormGroup, CustomFieldFormValue, ICustomizationFilters } from '@models/customization.types';
import { Condition } from '@models/enums/Condition';
import { CustomFieldTypes } from '@models/enums/CustomFieldTypes';
import { AwaiterService } from '@services/awaiter.service';
import { CustomFieldSectionService } from '@services/custom-field-section.service';
import { CustomizationService } from '@services/http/clients/customization.service';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ReactiveDropdownComponent } from '@shared/reactive-form-controls/reactive-dropdown/reactive-dropdown.component';
import { ReactiveFieldInputComponent } from '@shared/reactive-form-controls/reactive-field-input/reactive-field-input.component';
import { ReactiveListOptionsComponent } from '@shared/reactive-form-controls/reactive-list-options/reactive-list-options.component';
import {
    HelperInfoComponent,
    IconSanitizerService,
    ModalService,
    NoDataComponent,
    ObjectExtensionsService,
    SnackbarNotificationService
} from 'ngx-lib';
import { CustomizationFiltersComponent } from '../customization-filters/customization-filters.component';

@Component({
    selector: 'app-customization-custom-fields',
    standalone: true,
    templateUrl: './customization-custom-fields.component.html',
    styleUrls: ['./customization-custom-fields.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        DragDropModule,
        ReactiveFieldInputComponent,
        ReactiveDropdownComponent,
        ReactiveListOptionsComponent,
        CustomizationFiltersComponent,
        HelperInfoComponent,
        NoDataComponent
    ]
})
export class CustomizationCustomFieldsComponent {
    readonly entityName = input.required<string>();
    readonly entityTypeId = input.required<number>();
    readonly CustomFieldTypes = CustomFieldTypes;
    readonly customFieldTypes = signal<CustomFieldTypeView[]>([]);
    readonly entitySections = signal<EntityTypeCustomFieldSectionView[]>([]);
    readonly filters = signal<ICustomizationFilters>({});

    readonly hasUnsavedChanges = output<boolean>();

    readonly canAddNewField = computed(() => {
        return this.customizationService.canAddNew(this.filters());
    });

    readonly dragIndicatorIcon: SafeHtml;
    readonly archiveIcon: SafeHtml;
    readonly restoreIcon: SafeHtml;
    readonly lockedIcon: SafeHtml;
    readonly plusIcon: SafeHtml;

    readonly customFieldsForm = new FormGroup({
        fields: new FormArray<CustomFieldFormGroup>([])
    });

    readonly customFieldsArray = this.customFieldsForm.controls.fields;
    readonly fieldsDirty = computed(() => {
        this.formValueSignal();
        const initial = this.initialFields();

        const current = this.customFieldsArray.controls.map((group) => {
            const field = group.getRawValue();
            const sectionId =
                field.entityTypeCustomFieldSection?.id ?? field.entityTypeCustomFieldSectionId ?? undefined;
            return {
                id: field.id ?? null,
                organizationId: field.organizationId,
                organizationName: field.organizationName,
                entityTypeId: field.entityTypeId ?? 0,
                name: field.name ?? '',
                description: field.description ?? '',
                customFieldTypeId: field.customFieldTypeId ?? 0,
                customFieldTypeName: field.customFieldTypeName ?? '',
                entityTypeCustomFieldSectionId: sectionId ?? null,
                isActive: field.isActive ?? true,
                order: field.order ?? 0,
                isReserved: field.isReserved ?? false,
                options: field.options,
                entityCustomFieldValues: field.entityCustomFieldValues
            };
        }) as EntityTypeCustomFieldView[];

        return !this.customizationService.areFieldsEqual(current, initial);
    });

    readonly hasNoCustomFields = computed(() => {
        if (!this.hasLoadedInitially()) return false;
        const formValue = this.formValueSignal();
        const fields = formValue?.fields as CustomFieldFormValue[] | undefined;
        return !fields || fields.length === 0;
    });

    private readonly initialFields = signal<EntityTypeCustomFieldView[]>([]);
    private readonly currentEntityTypeId = signal<number | null>(null);
    private readonly hasLoadedInitially = signal<boolean>(false);
    private readonly formValueSignal = toSignal(this.customFieldsForm.valueChanges, {
        initialValue: this.customFieldsForm.value
    });

    private readonly snackbar = inject(SnackbarNotificationService);
    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly entityTypeCustomFieldService = inject(EntityTypeCustomFieldService);

    private readonly awaiter = inject(AwaiterService);
    private readonly customizationService = inject(CustomizationService);
    private readonly customFieldSectionService = inject(CustomFieldSectionService);
    private readonly modalService = inject(ModalService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);

    constructor() {
        this.dragIndicatorIcon = this.iconSanitizer.getIcon('dragIndicator');
        this.archiveIcon = this.iconSanitizer.getIcon('archive');
        this.restoreIcon = this.iconSanitizer.getIcon('restore');
        this.lockedIcon = this.iconSanitizer.getIcon('locked');
        this.plusIcon = this.iconSanitizer.getIcon('plus');

        effect(async () => {
            const entityTypeId = this.entityTypeId();
            if (!entityTypeId) return;

            const currentEntityId = this.currentEntityTypeId();
            if (currentEntityId === entityTypeId) return;

            this.currentEntityTypeId.set(entityTypeId);
            this.customFieldsArray.clear();
            this.initialFields.set([]);
            this.hasLoadedInitially.set(false);

            await this.awaiter.awaitAction('Loading Custom Fields', async () => {
                const archiveOptions = await this.customizationService.getArchiveOptions();
                const defaultActiveStatus = archiveOptions?.find((s) => s.id === Condition.Active);
                this.filters.set(defaultActiveStatus ? { status: defaultActiveStatus } : {});

                await this.loadInitialData(entityTypeId);
                await this.onFiltersChange(this.filters());
            });
        });

        effect(() => {
            this.hasUnsavedChanges.emit(this.fieldsDirty());
        });

        effect(() => {
            const formValue = this.formValueSignal();
            if (!formValue?.fields) return;

            const groups = this.customFieldsArray.controls;
            const fields = formValue.fields as CustomFieldFormValue[];

            for (let i = 0; i < groups.length && i < fields.length; i++) {
                const group = groups[i];
                const fieldValue = fields[i];
                if (!fieldValue) continue;

                if (fieldValue.customFieldType) {
                    const typeValue = fieldValue.customFieldType;
                    const currentId = group.controls.customFieldTypeId.value;
                    if (currentId !== typeValue.id) {
                        group.controls.customFieldTypeId.setValue(typeValue.id, { emitEvent: false });
                        group.controls.customFieldTypeName.setValue(typeValue.name ?? 'Type', { emitEvent: false });
                    }
                }

                if (fieldValue.entityTypeCustomFieldSection) {
                    const sectionValue = fieldValue.entityTypeCustomFieldSection;
                    const currentSectionId = group.controls.entityTypeCustomFieldSectionId.value;
                    if (currentSectionId !== sectionValue.id) {
                        group.controls.entityTypeCustomFieldSectionId.setValue(sectionValue.id, { emitEvent: false });
                    }
                }
            }
        });
    }

    async onFiltersChange(filters: ICustomizationFilters): Promise<void> {
        await this.awaiter.awaitAction('Loading Custom Fields', async () => {
            this.filters.set(filters);
            await this.loadCustomFields();
        });
    }

    addCustomField(): void {
        if (!this.canAddNewField()) return;

        const group = this.createCustomFieldGroup();
        this.customFieldsArray.insert(0, group);
        this.syncOrderControls();
        this.customFieldsForm.markAsDirty();
    }

    onFieldsDrop(event: CdkDragDrop<CustomFieldFormGroup[]>): void {
        if (event.previousIndex === event.currentIndex) return;
        const fieldControl = this.customFieldsArray.at(event.previousIndex);
        if (!fieldControl) return;

        this.customFieldsArray.removeAt(event.previousIndex);
        this.customFieldsArray.insert(event.currentIndex, fieldControl);
        this.syncOrderControls();
        this.customFieldsForm.markAsDirty();
    }

    async onSaveCustomFields(): Promise<void> {
        if (this.customFieldsForm.invalid) {
            this.customFieldsForm.markAllAsTouched();
            return;
        }

        if (this.validateListOptions()) {
            this.customFieldsForm.updateValueAndValidity();
            return;
        }

        await this.awaiter.awaitAction('Saving Custom Fields', async () => {
            const currentFields = this.getCurrentFormValues();
            if (currentFields.length) {
                const transformedFields = this.customizationService.prepareFieldsForSave(currentFields);
                const savedCustomFields = await this.entityTypeCustomFieldService.save(transformedFields);

                this.resetCustomFieldsForm(savedCustomFields);

                this.snackbar.success('Custom Fields saved successfully.');
                this.syncInitialFields();
                this.customFieldsForm.markAsPristine();
            }
        });
    }

    async onCancelCustomFields(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Cancel Editing',
            cancelCaption: 'Continue Edition',
            content: 'Are you sure you want to move on? Your changes will be lost',
            title: 'Unsaved changes'
        });

        if (!responseOk) return;

        this.resetCustomFieldsForm(this.initialFields());
    }

    async onToggleCondition(index: number, archive: boolean): Promise<void> {
        const control = this.customFieldsArray.at(index);
        if (!control) return;
        const id = control.controls.id.value;
        if (!id) return;

        const actionLabel = archive ? 'Archiving Custom Field' : 'Restoring Custom Field';
        const successMessage = archive
            ? `${control.controls.name.value ?? 'Field'} archived successfully.`
            : `${control.controls.name.value ?? 'Field'} restored successfully.`;

        await this.awaiter.awaitAction(actionLabel, async () => {
            if (archive) {
                await this.entityTypeCustomFieldService.deactivate(id);
            } else {
                await this.entityTypeCustomFieldService.activate(id);
            }

            const filters = this.filters();
            const shouldRemove =
                (archive && filters.status?.id === Condition.Active) ||
                (!archive && filters.status?.id === Condition.Archived);

            if (shouldRemove) {
                this.customFieldsArray.removeAt(index);
                this.syncOrderControls();
            } else {
                control.controls.isActive.setValue(!archive);
            }

            this.syncInitialFields();
            this.customFieldsForm.markAsPristine();
            this.customFieldsForm.updateValueAndValidity({ emitEvent: false });
            this.snackbar.success(successMessage);
        });
    }

    private async loadInitialData(entityTypeId: number): Promise<void> {
        await this.awaiter.awaitAction('Loading Custom Field Types', async () => {
            const fieldTypes = await this.customizationService.getCustomFieldTypes();
            this.customFieldTypes.set(fieldTypes);
            const sections = await this.customFieldSectionService.getByEntityId(entityTypeId);
            this.entitySections.set(sections || []);
        });
    }

    private async loadCustomFields(): Promise<void> {
        if (!this.entitySections().length) {
            return;
        }

        const searchBody = this.buildSearchConfig();

        const fields = await this.entityTypeCustomFieldService.search(searchBody);
        const data = fields || [];

        this.resetCustomFieldsForm(data);
        const current = this.getCurrentFormValues();
        this.initialFields.set(
            current.map((item) => this.objectExtensionService.clone(item) as EntityTypeCustomFieldView)
        );
        this.hasLoadedInitially.set(true);
    }

    private buildSearchConfig(): SearchEntityTypeCustomFieldParameters {
        const filterValues = this.filters();

        return {
            entityTypeId: this.entityTypeId(),
            isActive: filterValues.status?.value,
            isReserved: filterValues.source?.value
        } as SearchEntityTypeCustomFieldParameters;
    }

    private resetCustomFieldsForm(fields: EntityTypeCustomFieldView[]): void {
        this.customFieldsArray.clear();
        for (const field of fields) {
            const group = this.createCustomFieldGroup(field);
            this.customFieldsArray.push(group);
        }
        this.syncOrderControls();
        this.customFieldsForm.markAsPristine();
        this.customFieldsForm.updateValueAndValidity({ emitEvent: true });
    }

    private createCustomFieldGroup(value?: EntityTypeCustomFieldView): CustomFieldFormGroup {
        const defaultType =
            this.customFieldTypes()?.[0] ?? ({ id: CustomFieldTypes.String, name: 'Text' } as CustomFieldTypeView);
        return new FormGroup({
            id: new FormControl<number | null>(value?.id ?? null),
            organizationId: new FormControl<number | undefined>(value?.organizationId ?? undefined, {
                nonNullable: false
            }),
            organizationName: new FormControl<string | undefined>(value?.organizationName ?? undefined, {
                nonNullable: false
            }),
            entityTypeId: new FormControl<number>(value?.entityTypeId ?? this.entityTypeId(), {
                nonNullable: true
            }),
            name: new FormControl<string>(value?.name ?? '', {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(30)]
            }),
            description: new FormControl<string>(value?.description ?? '', {
                nonNullable: true,
                validators: [Validators.maxLength(120)]
            }),
            customFieldTypeId: new FormControl<number>(value?.customFieldTypeId ?? defaultType.id, {
                nonNullable: true,
                validators: [Validators.required]
            }),
            customFieldTypeName: new FormControl<string>(value?.customFieldTypeName ?? defaultType.name ?? 'Type', {
                nonNullable: true
            }),
            customFieldType: new FormControl<CustomFieldTypeView | null>(
                value ? this.getTypeOptionById(value.customFieldTypeId) : defaultType
            ),
            entityTypeCustomFieldSectionId: new FormControl<number | undefined>(
                value?.entityTypeCustomFieldSectionId ?? this.entitySections()?.[0]?.id
            ),
            entityTypeCustomFieldSection: new FormControl<EntityTypeCustomFieldSectionView | null>(
                value
                    ? (this.getSectionById(value.entityTypeCustomFieldSectionId) ?? null)
                    : (this.entitySections()[0] ?? null)
            ),
            isActive: new FormControl<boolean>(value?.isActive ?? true, { nonNullable: true }),
            order: new FormControl<number>(value?.order ?? this.customFieldsArray.length, {
                nonNullable: true
            }),
            isReserved: new FormControl<boolean>(value?.isReserved ?? false, { nonNullable: true }),
            options: new FormControl<string[]>(value?.options ?? [], {
                nonNullable: true
            }),
            entityCustomFieldValues: new FormControl<EntityCustomFieldValueView[] | undefined>(
                value?.entityCustomFieldValues ?? undefined,
                { nonNullable: false }
            )
        });
    }

    private getTypeOptionById(id?: number): CustomFieldTypeView {
        const list = this.customFieldTypes();
        return (
            list.find((option) => option.id === id) ??
            ({ id: CustomFieldTypes.String, name: 'Text' } as CustomFieldTypeView)
        );
    }

    private getSectionById(id?: number): EntityTypeCustomFieldSectionView | undefined {
        if (!id) return this.entitySections()?.[0];
        return this.entitySections().find((s) => s.id === id) ?? this.entitySections()?.[0];
    }

    private validateListOptions(): boolean {
        let hasError = false;
        for (const group of this.customFieldsArray.controls) {
            const typeId = group.controls.customFieldTypeId.value;
            const isListType = typeId === CustomFieldTypes.List || typeId === CustomFieldTypes.MultipleSelectionList;
            if (!isListType) continue;

            const options = group.controls.options.value ?? [];
            const hasValidOption = options.some((option) => typeof option === 'string' && option.trim().length > 0);
            if (hasValidOption) {
                group.controls.options.setErrors(null);
            } else {
                group.controls.options.setErrors({ required: true });
                group.controls.options.markAsTouched();
                hasError = true;
            }
        }
        return hasError;
    }

    private getCurrentFormValues(): EntityTypeCustomFieldView[] {
        return this.customFieldsArray.controls.map((group) => {
            const field = group.getRawValue();
            return {
                id: field.id ?? null,
                organizationId: field.organizationId,
                organizationName: field.organizationName,
                entityTypeId: field.entityTypeId ?? 0,
                name: field.name ?? '',
                description: field.description ?? '',
                customFieldTypeId: field.customFieldTypeId ?? 0,
                customFieldTypeName: field.customFieldTypeName ?? '',
                entityTypeCustomFieldSectionId: field.entityTypeCustomFieldSectionId,
                isActive: field.isActive ?? true,
                order: field.order ?? 0,
                isReserved: field.isReserved ?? false,
                options: field.options,
                entityCustomFieldValues: field.entityCustomFieldValues
            } as unknown as EntityTypeCustomFieldView;
        });
    }

    private syncInitialFields(): void {
        const current = this.getCurrentFormValues();
        this.initialFields.set(
            current.map((item) => this.objectExtensionService.clone(item) as EntityTypeCustomFieldView)
        );
    }

    private syncOrderControls(): void {
        this.customFieldsArray.controls.forEach((group, index) => {
            const expectedOrder = index + 1;
            if (group.controls.order.value !== expectedOrder) {
                group.controls.order.setValue(expectedOrder, { emitEvent: false });
            }
        });
    }
}
